import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  ApolloClient,
  ApolloProvider,
  HttpLink,
  from,
  useReactiveVar,
} from '@apollo/client';
import * as Sentry from '@sentry/react';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { useKitbagAuth } from '@statsbomb/kitbag-auth';
import { ui_networkError, cache } from '.';
import { timingLink } from '../utils/helpers/timingLink';
import { PRODUCTION } from '../components/app/index.constants';

const ApolloClientProvider = ({ apiUri = '', children = null }) => {
  const { getAccessTokenSilently } = useKitbagAuth();
  const ui_networkErrorRV = useReactiveVar(ui_networkError);

  const client = useMemo(() => {
    const httpLink = new HttpLink({
      fetch,
      uri: apiUri,
    });

    /* 
    
    https://docs.sentry.io/platforms/javascript/usage/
    
    Usage
      Sentry.captureException(exception, captureContext?)

      Sentry.captureException(error, {
        user: { id: "123", email: "user@example.com" },
        level: "error",
        extra: { debugInfo: "Additional details" },
        tags: { section: "checkout" }
      });

      CaptureContext {
        user?: {
          id?: string | number,
          email?: string,
          ip_address?: string,
          username?: string,
        },
        level?: "fatal" | "error" | "warning" | "log" | "info" | "debug",
        extra?: Record<string, unknown>,
        tags?: Record<string, string>,
        contexts?: Record<string, Record<string, unknown>>,
        fingerprint?: string[],
      }
    */
    const errorLink = onError(({ graphQLErrors, networkError }) => {
      if (graphQLErrors)
        graphQLErrors.forEach(({ message, locations, path }) => {
          // eslint-disable-next-line no-console
          console.log(
            `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(
              locations
            )}, Path: ${path}`
          );

          if (process.env.NODE_ENV === PRODUCTION) {
            Sentry.captureException(new Error(message), {
              extra: {
                locations,
                path,
                type: 'GraphQL error',
              },
            });
          }
        });
      if (networkError) {
        // eslint-disable-next-line no-console
        console.log(`[Network error]: ${networkError}`);
        if (process.env.NODE_ENV === PRODUCTION) {
          Sentry.captureException(networkError);
        }
        ui_networkError(true);
      } else if (ui_networkErrorRV) {
        ui_networkError(false);
      }
    });

    const authLink = setContext(async (_, { headers }) => {
      const token = await getAccessTokenSilently();

      return {
        headers: {
          ...headers,
          authorization: token ? `Bearer ${token}` : '',
        },
      };
    });

    const addPath = setContext(async (_, { headers }) => {
      const { pathname } = window.location;
      return {
        headers: {
          ...headers,
          link: pathname,
        },
      };
    });

    return new ApolloClient({
      link: from([errorLink, authLink, timingLink, addPath, httpLink]),
      cache,
    });
  }, []);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

ApolloClientProvider.propTypes = {
  apiUri: PropTypes.string,
  children: PropTypes.element,
};

export default ApolloClientProvider;
